import { useAppDispatch, useAppSelector } from 'app/hooks';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import StoreSettingDetailFilter from '../components/StoreSettingDetailFilter';
import StoreSettingDetailTable from '../components/StoreSettingDetailTable';
import { COLLAPSE_ALL, EXPAND_ALL } from '../constants';
import { Restaurant, StoreSettingDetailGetAllRequestPayload } from '../models';
import {
  selectStoreSettingDetailData,
  selectStoreSettingDetailIsReload,
  storeSettingDetailActions,
} from '../storeSettingDetailSlice';
import storeSettingService from '../storeSettingService';
import './StoreSettingDetailPage.scss';

export const COLUMN_WIDTH = [8, 10, 6, 10, 10, 4, 4, 6, 4, 6, 4, 4, 6, 4, 6, 4, 4];

function StoreSettingDetailPage() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const { showGlobalModal } = useGlobalModal();
  const history = useHistory();

  // state
  const [restaurant, setRestaurant] = useState<Restaurant>();

  // selector
  const storeSettingDetailData = useAppSelector(selectStoreSettingDetailData);
  const isReloadData = useAppSelector(selectStoreSettingDetailIsReload);
  const isEditMode = useAppSelector(selectIsEditMode);

  // ref
  const filterPayload = useRef<StoreSettingDetailGetAllRequestPayload>();

  function loadInitData() {
    (async () => {
      try {
        const restaurantResponse = await storeSettingService.getItem(Number(restaurantId) || 0);
        if (restaurantResponse.data === null) {
          history.push('/404');
          return;
        }
        if (restaurantResponse.data.sale_type_id === null || restaurantResponse.data.counter_pattern_id === null) {
          showGlobalModal(() => (
            <AlertModal
              title={t('App.warning')}
              message={t('StoreSettingDetailPage.warn_input_saletype_and_counter_pattern_required')}
              onClose={() => {
                history.push(`/store/store-setting`);
              }}
            />
          ));
          return;
        }
        setRestaurant(restaurantResponse.data);
      } catch (e) {
        // console.log(e);
      }
    })();
  }

  useEffect(() => {
    loadInitData();
    dispatch(storeSettingDetailActions.resetData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isReloadData && filterPayload.current) {
      dispatch(storeSettingDetailActions.fetchData(filterPayload.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReloadData]);

  function handleExpandAll() {
    dispatch(storeSettingDetailActions.setExpandAll(EXPAND_ALL));
  }

  function handleCollapseAll() {
    dispatch(storeSettingDetailActions.setExpandAll(COLLAPSE_ALL));
  }

  function handleShowOnlyChanges() {
    dispatch(storeSettingDetailActions.toggleDisplayType());
  }

  function handleSearchSubmit(payload: StoreSettingDetailGetAllRequestPayload) {
    filterPayload.current = payload;
    dispatch(storeSettingDetailActions.fetchData(payload));
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="page-header">
          <StatusHeader />
        </div>
        <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : undefined}`}>
          <div className="page-content store-setting-page d-flex flex-column">
            <div>
              <FeatureTitleBar title={t('StoreSettingDetailPage.head_title')}></FeatureTitleBar>
            </div>
            <div className="d-flex mb-3">
              <div className="me-4">
                {restaurant?.name} ({restaurant?.cd})
              </div>
              <div className="me-4">
                {t('StoreSettingDetailPage.sale_type')}：{restaurant?.sale_type_name}
              </div>
              <div className="me-4">
                {t('StoreSettingDetailPage.counter_pattern')}：{restaurant?.counter_pattern_cd}
              </div>
            </div>

            {restaurant && <StoreSettingDetailFilter restaurant={restaurant} onSubmit={handleSearchSubmit} />}

            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="d-inline-block me-5">
                  <Button
                    variant="link"
                    className="px-0 text-decoration-none"
                    onClick={handleExpandAll}
                    style={{ minWidth: 'auto' }}
                  >
                    {t('StoreSettingDetailPage.expand_all')}
                  </Button>
                  <span>／</span>
                  <Button
                    variant="link"
                    className="px-0 text-decoration-none"
                    onClick={handleCollapseAll}
                    style={{ minWidth: 'auto' }}
                  >
                    {t('StoreSettingDetailPage.collapse_all')}
                  </Button>
                </div>
                <div className="d-inline-block">
                  <Button
                    variant="link"
                    className="px-0 text-decoration-none"
                    onClick={handleShowOnlyChanges}
                    style={{ minWidth: 'auto' }}
                  >
                    {t('StoreSettingDetailPage.show_changes_only')}
                  </Button>
                </div>
              </div>
              <div className="d-flex align-items-center">
                <div className="border-individual-setting d-inline-block me-3"></div>
                <span className="">{t('StoreSettingDetailPage.individual_store_settings_available')}</span>
              </div>
            </div>

            <StoreSettingDetailTable data={storeSettingDetailData} />
          </div>
        </div>
      </div>
    </>
  );
}

export default StoreSettingDetailPage;
